import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  ServerIcon,
  CircleStackIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: number;
  services: {
    api: 'online' | 'offline';
    database: 'online' | 'offline';
    redis: 'online' | 'offline';
    storage: 'online' | 'offline';
  };
  performance: {
    cpu: number;
    memory: number;
    disk: number;
  };
  database: {
    connections: number;
    maxConnections: number;
    queryTime: number;
  };
  jobs: {
    active: number;
    waiting: number;
    failed: number;
  };
}

interface ErrorLog {
  id: string;
  timestamp: Date;
  level: 'error' | 'warning' | 'info';
  message: string;
  service: string;
  count: number;
}

const AdminSystemPage: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState<'all' | 'error' | 'warning' | 'info'>('all');

  useEffect(() => {
    loadSystemData();
    const interval = setInterval(loadSystemData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemData = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API calls
      const mockHealth: SystemHealth = {
        status: 'healthy',
        uptime: 1458720,
        services: {
          api: 'online',
          database: 'online',
          redis: 'online',
          storage: 'online',
        },
        performance: {
          cpu: 42,
          memory: 68,
          disk: 45,
        },
        database: {
          connections: 23,
          maxConnections: 100,
          queryTime: 12.5,
        },
        jobs: {
          active: 5,
          waiting: 12,
          failed: 2,
        },
      };

      const mockLogs: ErrorLog[] = [
        {
          id: '1',
          timestamp: new Date(Date.now() - 300000),
          level: 'error',
          message: 'Failed to process payment webhook',
          service: 'Payment Service',
          count: 3,
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 600000),
          level: 'warning',
          message: 'High memory usage detected',
          service: 'System Monitor',
          count: 1,
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 900000),
          level: 'error',
          message: 'Database connection timeout',
          service: 'Database',
          count: 2,
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 1200000),
          level: 'info',
          message: 'System backup completed successfully',
          service: 'Backup Service',
          count: 1,
        },
      ];

      setHealth(mockHealth);
      setLogs(mockLogs);
    } catch (error) {
      console.error('Failed to load system data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  const getStatusIcon = (status: string) => {
    if (status === 'online' || status === 'healthy') {
      return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    }
    return <XCircleIcon className="w-5 h-5 text-red-600" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'online' || status === 'healthy') return 'text-green-600';
    if (status === 'warning') return 'text-yellow-600';
    return 'text-red-600';
  };

  const getLogBadgeColor = (level: string) => {
    if (level === 'error') return 'bg-red-100 text-red-800';
    if (level === 'warning') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const filteredLogs = logs.filter((log) => logFilter === 'all' || log.level === logFilter);

  if (loading || !health) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">Loading system data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">System Monitoring</h1>
        <p className="mt-2 text-gray-600">Platform health and performance metrics</p>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <ServerIcon className="w-8 h-8 text-blue-600" />
              {getStatusIcon(health.status)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">System Status</h3>
            <p className={`text-2xl font-bold ${getStatusColor(health.status)}`}>
              {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
            </p>
            <p className="text-xs text-gray-500 mt-2">Uptime: {formatUptime(health.uptime)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <CircleStackIcon className="w-8 h-8 text-purple-600" />
              {getStatusIcon(health.services.database)}
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Database</h3>
            <p className={`text-2xl font-bold ${getStatusColor(health.services.database)}`}>
              {health.database.connections}/{health.database.maxConnections}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Avg query: {health.database.queryTime}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <ClockIcon className="w-8 h-8 text-orange-600" />
              <span className="text-sm font-medium text-gray-600">
                {health.jobs.failed > 0 && (
                  <span className="text-red-600">{health.jobs.failed} failed</span>
                )}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Background Jobs</h3>
            <p className="text-2xl font-bold text-gray-900">{health.jobs.active}</p>
            <p className="text-xs text-gray-500 mt-2">{health.jobs.waiting} waiting</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
              <span className="text-sm font-medium text-red-600">
                {logs.filter((l) => l.level === 'error').length} errors
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Error Logs</h3>
            <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
            <p className="text-xs text-gray-500 mt-2">Last 24 hours</p>
          </CardContent>
        </Card>
      </div>

      {/* Services Status */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(health.services).map(([service, status]) => (
              <div
                key={service}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(status)}
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {service}
                  </span>
                </div>
                <span className={`text-xs font-medium ${getStatusColor(status)} capitalize`}>
                  {status}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance</h2>
          <div className="space-y-4">
            {Object.entries(health.performance).map(([metric, value]) => (
              <div key={metric}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 capitalize">{metric}</span>
                  <span className="text-sm font-bold text-gray-900">{value}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      value < 70
                        ? 'bg-green-500'
                        : value < 85
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Error Logs */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Logs</h2>
            <div className="flex gap-2">
              {(['all', 'error', 'warning', 'info'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setLogFilter(filter)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg ${
                    logFilter === filter
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getLogBadgeColor(
                          log.level
                        )}`}
                      >
                        {log.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.service}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.message}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.count > 1 && (
                        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">
                          ×{log.count}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={loadSystemData}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSystemPage;
